import * as React from "react";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { Formik, Form } from "formik";
import { useQuery } from "react-query";
import {
  JobRequestFileCategory,
  JobRequestNewFile,
  LltsLanguage,
  MachineTranslationService,
  UtilsService
} from "gen/clients/llts";
import { SelectOption } from "@mui/base";
import { SELECT_OPTION_COMPARATOR } from "utils/stringUtils";
import { useS3FileUpload } from "hooks/useS3FileUpload/useS3FileUpload2";
import { TextInputField } from "components/formikFields/TextInputField/TextInputField";
import { SelectOneAutocompleteField } from "components/formikFields/SelectOneAutocompleteField/SelectOneAutocompleteField";
import { DropzoneField } from "components/formikFields/DropzoneField/DropzoneField";
import { LoadingButton } from "components/LoadingButton/LoadingButton";
import Button from "@mui/material/Button";
import { AlertDialog } from "components/AlertDialog/AlertDialog";
import { LinearProgressWithLabel } from "components/LinearProgressWithLabel/LinearProgressWithLabel";
import { SnackbarApiError } from "components/SnackbarApiError/SnackbarApiError";
import { LoadingPage } from "components/LoadingPage/LoadingPage";
import { ErrorPage } from "components/ErrorPage/ErrorPage";

interface Props {
  onClose: () => void;
  onSuccess: () => void;
}

enum FieldName {
  projectName = "projectName",
  sourceLanguage = "sourceLanguage",
  files = "files",
  watermarkText = "watermarkText"
}

interface FormValues {
  [FieldName.projectName]: string;
  [FieldName.sourceLanguage]: SelectOption<string> | null;
  [FieldName.files]: File[];
  [FieldName.watermarkText]: string;
}

const MAX_TOTAL_FILES_SIZE_BYTES = 250 * 1024 * 1024; // 250 MB
const MAX_WATERMARK_TEXT_LENGTH = 30;

const ACCEPTED_FILE_TYPES = [
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
  "application/vnd.openxmlformats-officedocument.presentationml.presentation", // .pptx
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
  "application/pdf", // .pdf
  ".docx",
  ".pptx",
  ".xlsx",
  ".pdf"
];

const AddWatermarkStep: React.FC<Props> = ({ onClose, onSuccess }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const {
    invoke: uploadFiles,
    isUploading: areFilesUploading,
    error: filesUploadError,
    uploadProgress,
    reset: resetS3FileUpload
  } = useS3FileUpload();

  const {
    data: mtSettings,
    isLoading: areMtSettingsLoading,
    error: mtSettingsError
  } = useQuery(["getMtSettings"], {
    queryFn: MachineTranslationService.getMachineTranslationSettings
  });

  const {
    data: lltsLanguages,
    isLoading: areLltsLanguagesLoading,
    error: lltsLanguagesError
  } = useQuery(["listLltsLanguages"], {
    queryFn: () => UtilsService.listLltsLanguages({})
  });

  const isLoading = areMtSettingsLoading || areLltsLanguagesLoading;
  const error = mtSettingsError || lltsLanguagesError;

  const lltsLanguageById: Record<string, LltsLanguage> = React.useMemo(
    () =>
      (lltsLanguages || []).reduce(
        (prevValue, currentValue) => {
          // eslint-disable-next-line no-param-reassign
          prevValue[currentValue.id] = currentValue;
          return prevValue;
        },
        {} as Record<string, LltsLanguage>
      ),
    [lltsLanguages]
  );

  const languageOptions = React.useMemo(
    () =>
      mtSettings?.supportedLltsLanguages
        ?.filter(langId => lltsLanguageById[langId] && !lltsLanguageById[langId].isDisabled)
        .map(langId => ({
          label: lltsLanguageById[langId]?.name || langId,
          value: langId
        }))
        .sort(SELECT_OPTION_COMPARATOR) || [],
    [mtSettings, lltsLanguageById]
  );

  const initialValues: FormValues = {
    [FieldName.projectName]: "",
    [FieldName.sourceLanguage]: null,
    [FieldName.files]: [],
    [FieldName.watermarkText]: ""
  };

  const validate = React.useCallback(
    (values: FormValues) => {
      const errors: Partial<Record<FieldName, string>> = {};

      if (values[FieldName.files].length === 0) {
        errors[FieldName.files] = t("machineTranslation.aiToolbox.validation.filesRequired");
      }

      const totalFilesSize = values[FieldName.files].reduce((prev, currentFile) => prev + currentFile.size, 0);
      if (totalFilesSize >= MAX_TOTAL_FILES_SIZE_BYTES) {
        errors[FieldName.files] = t("machineTranslation.aiToolbox.validation.maxTotalFilesSize");
      }

      if (!values[FieldName.watermarkText].trim()) {
        errors[FieldName.watermarkText] = t("machineTranslation.aiToolbox.watermarkText.required");
      } else if (values[FieldName.watermarkText].length > MAX_WATERMARK_TEXT_LENGTH) {
        errors[FieldName.watermarkText] = t("common.validation.maxLength", { max: MAX_WATERMARK_TEXT_LENGTH });
      }

      return errors;
    },
    [t]
  );

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      resetS3FileUpload();

      await uploadFiles({
        files: values[FieldName.files],
        onSuccess: async fileKeys => {
          const jobRequestFiles: JobRequestNewFile[] = values[FieldName.files].map((file, index) => ({
            fileName: file.name,
            category: JobRequestFileCategory.SOURCE_DOCUMENT,
            fileKey: fileKeys[index],
            languageId: values[FieldName.sourceLanguage]!.value
          }));

          await MachineTranslationService.addWatermark({
            requestBody: {
              requestName: values[FieldName.projectName],
              sourceLanguageId: values[FieldName.sourceLanguage]!.value,
              files: jobRequestFiles,
              watermarkText: values[FieldName.watermarkText]
            }
          });

          onSuccess();
        }
      });
    },
    [onSuccess, resetS3FileUpload, uploadFiles]
  );

  if (isLoading) {
    return <LoadingPage />;
  }

  if (error) {
    return <ErrorPage apiError={error} />;
  }

  return (
    <>
      {filesUploadError && <SnackbarApiError error={filesUploadError} />}
      <Formik initialValues={initialValues} validate={validate} onSubmit={onSubmit}>
        {({ isSubmitting }) => (
          <Form noValidate={true} autoComplete="off" autoCorrect="off">
            <Stack spacing={3}>
              <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                {t("machineTranslation.aiToolbox.actions.addWatermark.panelDescription")}
              </Typography>

              <TextInputField
                name={FieldName.projectName}
                label={t("machineTranslation.aiToolbox.projectName.label")}
                helperText={t("machineTranslation.aiToolbox.projectName.helperText")}
                required={true}
                requiredMessage={t("machineTranslation.aiToolbox.projectName.required")}
                preventCharacters={[";"]}
                preventCharactersMessage={t("common.validation.semicolonNotAllowed")}
                maxLength={140}
                maxLengthMessage={t("common.validation.projectNameMaxLength")}
              />

              <SelectOneAutocompleteField
                name={FieldName.sourceLanguage}
                label={t("machineTranslation.aiToolbox.sourceLanguage.label")}
                helperText={t("machineTranslation.aiToolbox.sourceLanguage.helperText")}
                options={languageOptions}
                required={true}
                requiredMessage={t("machineTranslation.aiToolbox.sourceLanguage.required")}
              />

              <TextInputField
                name={FieldName.watermarkText}
                label={t("machineTranslation.aiToolbox.watermarkText.label")}
                helperText={t("machineTranslation.aiToolbox.watermarkText.helperText")}
                required={true}
                requiredMessage={t("machineTranslation.aiToolbox.watermarkText.required")}
                maxLength={MAX_WATERMARK_TEXT_LENGTH}
              />

              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                  {t("machineTranslation.aiToolbox.watermarkFiles.label")}
                </Typography>
                <DropzoneField
                  name={FieldName.files}
                  dropzoneText={t("machineTranslation.aiToolbox.watermarkFiles.dropzoneText")}
                  helperText={t("machineTranslation.aiToolbox.watermarkFiles.helperText")}
                  fileUploadLimits={{
                    maxFileCount: mtSettings?.maxFilesPerRequest,
                    maxFileSize: mtSettings ? mtSettings.maxFileSizeMB * 1024 * 1024 : undefined,
                    acceptedFiles: ACCEPTED_FILE_TYPES
                  }}
                />
              </Box>

              <Stack direction="row" spacing={1} justifyContent="center" pt={5}>
                <LoadingButton isLoading={isSubmitting}>{t("machineTranslation.aiToolbox.submitBtn")}</LoadingButton>
                <Button onClick={onClose}>{t("common.cancel")}</Button>
              </Stack>

              <Box pt={4}>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.grey["700"] }}
                  dangerouslySetInnerHTML={{ __html: t("machineTranslation.aiToolbox.disclaimer") }}
                />
              </Box>
            </Stack>
          </Form>
        )}
      </Formik>

      {areFilesUploading && (
        <AlertDialog title={t("machineTranslation.aiToolbox.uploadingAlert.title")}>
          <Stack spacing={1}>
            <Box>{t("machineTranslation.aiToolbox.uploadingAlert.message")}</Box>
            <Box sx={{ width: "100%" }}>
              <LinearProgressWithLabel value={uploadProgress} />
            </Box>
          </Stack>
        </AlertDialog>
      )}
    </>
  );
};

export { AddWatermarkStep };
